import { useCallback, useMemo, useState } from 'react';

/**
 * Row selection for a Table. Selection is scoped to the rows currently
 * passed in: when those change (page switch, filter change) the selection is
 * dropped, so a bulk action can never target rows the user cannot see.
 *
 * @param {Array<Object>} rows - The rows currently rendered.
 * @param {Object} [options]
 * @param {string|((row: Object) => string)} [options.rowId='id'] - Property name or getter for the row id.
 * @returns {{
 *   selectedIds: string[],
 *   isSelected: (id: string) => boolean,
 *   toggleOne: (id: string) => void,
 *   toggleAll: () => void,
 *   clear: () => void,
 *   allChecked: boolean,
 *   someChecked: boolean,
 * }}
 */
export function useTableSelection(rows, { rowId = 'id' } = {}) {
	const [selectedIds, setSelectedIds] = useState([]);

	const getId = useMemo(() => (typeof rowId === 'function' ? rowId : (row) => row[rowId]), [rowId]);

	const visibleIds = useMemo(() => rows.map(getId).filter(Boolean), [rows, getId]);

	// Identity of the visible set, not of the array: re-rendering with an equal
	// list must not drop the selection, while a page or filter change must.
	const visibleKey = visibleIds.join('|');

	const [lastVisibleKey, setLastVisibleKey] = useState(visibleKey);
	if (visibleKey !== lastVisibleKey) {
		setLastVisibleKey(visibleKey);
		if (selectedIds.length > 0) setSelectedIds([]);
	}

	const clear = useCallback(() => setSelectedIds([]), []);

	const toggleOne = useCallback((id) => {
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	}, []);

	const allChecked = visibleIds.length > 0 && selectedIds.length === visibleIds.length;
	const someChecked = selectedIds.length > 0 && !allChecked;

	const toggleAll = useCallback(() => {
		setSelectedIds(allChecked ? [] : visibleIds);
	}, [allChecked, visibleIds]);

	const isSelected = useCallback((id) => selectedIds.includes(id), [selectedIds]);

	return useMemo(
		() => ({ selectedIds, isSelected, toggleOne, toggleAll, clear, allChecked, someChecked }),
		[selectedIds, isSelected, toggleOne, toggleAll, clear, allChecked, someChecked],
	);
}
